import b from 'b_';
import PropTypes from 'prop-types';
import React from 'react';

import { RADIO_SIZES, RADIO_THEMES, RADIO_TYPES } from '../constants';
import Link from '../link/link';

import './radio.scss';

export class Radio extends React.PureComponent {
    static propTypes = {
        checked: PropTypes.bool,
        children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        disabled: PropTypes.bool,
        focused: PropTypes.bool,
        mix: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        name: PropTypes.string,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        size: PropTypes.oneOf(Object.values(RADIO_SIZES)),
        theme: PropTypes.oneOf(Object.values(RADIO_THEMES)),
        type: PropTypes.oneOf(Object.values(RADIO_TYPES)),
        value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
        role: PropTypes.string,
        'aria-label': PropTypes.string,
        'aria-selected': PropTypes.string,
    };

    static defaultProps = {
        size: RADIO_SIZES.MEDIUM,
        theme: RADIO_THEMES.NORMAL,
        type: RADIO_TYPES.RADIO,
    };

    state = {
        focused: this.props.focused,
        hasImage: false,
    };

    UNSAFE_componentWillMount() {
        this.renderers = {
            link: this.renderTypeLink,
            radio: this.renderTypeRadio,
            button: this.renderTypeButton,
            tab: this.renderTypeTab,
            text: this.renderTypeText,
            list: this.renderTypeList,
            bullet: this.renderTypeBullet,
        };
    }

    componentDidMount() {
        if (this.state.focused) {
            this.focus();
        }

        if (this.refLabel.current) {
            const hasImg = this.refLabel.current.querySelector('.paragraph:first-child img:first-child');

            if (hasImg) {
                // eslint-disable-next-line react/no-did-mount-set-state
                this.setState({ hasImage: true });
            }
        }
    }

    onChange = (e) => {
        this.focus();

        if (this.props.onChange) {
            this.props.onChange(e);
        }
    };

    onFocus = () => {
        this.setState({ focused: true });
        this.focus();
    };

    onBlur = () => {
        this.setState({ focused: false });
    };

    focus = () => {
        this.refs.radioInput.focus();
    };

    renderInput = (inputProps, handlers, hidden) => {
        return (
            <input
                autoComplete="off"
                className="radio__control"
                hidden={hidden}
                ref="radioInput"
                type="radio"
                {...inputProps}
                {...handlers}
            />
        );
    };

    renderTypeRadio = (inputProps, handlers, children) => {
        return (
            <div>
                <span className="radio__box">
                    {this.renderInput(inputProps, handlers, false)}
                    <span className="radio__tick" />
                </span>
                {children}
            </div>
        );
    };

    renderTypeLink = (inputProps, handlers, children) => {
        return (
            <Link mix="radio__link" pseudo {...handlers}>
                {this.renderInput(inputProps, handlers, true)}
                {children}
            </Link>
        );
    };

    renderTypeTab = (inputProps, handlers, children) => {
        return (
            <Link mix="radio__tab" pseudo>
                {this.renderInput(inputProps, handlers, true)}
                {children}
            </Link>
        );
    };

    renderTypeBullet = (inputProps, handlers, children) => {
        return (
            <Link mix="radio__tab" pseudo>
                {this.renderInput(inputProps, handlers, true)}
                {children}
            </Link>
        );
    };

    renderTypeButton = (inputProps, handlers, children) => {
        return (
            <Link mix="radio__button" pseudo {...handlers}>
                {this.renderInput(inputProps, handlers, true)}
                {children}
            </Link>
        );
    };

    renderTypeList = (inputProps, handlers, children) => {
        return (
            <React.Fragment>
                <Link mix="radio__button" pseudo {...handlers}>
                    {this.renderInput(inputProps, handlers, true)}
                    {children}
                </Link>
                <svg className="radio__button-triangle">
                    <path d="m0,42 V 0 l12,21 z" fill="currentColor" />
                </svg>
            </React.Fragment>
        );
    };

    renderTypeText = (inputProps, handlers, children) => {
        const { hasImage } = this.state;

        return (
            <span className={b('radio', 'text', { enabled: !this.props.disabled })} {...handlers}>
                <div className={b('radio', 'wrapper', { image: hasImage })}>
                    {this.renderInput(inputProps, handlers, false)}
                    <span className="radio__custom-tick" />
                </div>
                {children}
            </span>
        );
    };

    refLabel = React.createRef();

    render() {
        const {
            children,
            checked,
            disabled,
            mix = '',
            name,
            onClick,
            size,
            theme,
            type,
            value,
            role = 'radio',
            'aria-label': ariaLabel,
            'aria-selected': ariaSelected,
        } = this.props;
        const { focused } = this.state;

        const className = b('radio', {
            checked,
            disabled,
            focused,
            size,
            theme,
            type,
        });
        const radioInputProps = {
            checked,
            disabled,
            name,
            value,
        };
        const radioHandlers = {
            onBlur: this.onBlur,
            onChange: this.onChange,
            onFocus: this.onFocus,
            onClick,
        };

        return (
            <label
                aria-checked={checked}
                aria-label={ariaLabel}
                aria-selected={ariaSelected}
                className={`${className} ${mix}`}
                ref={this.refLabel}
                role={role}
                tabIndex={0}
            >
                {this.renderers[type](radioInputProps, radioHandlers, children)}
            </label>
        );
    }
}

export default Radio;
